import DOMPurify from "dompurify"

// Own instance, so the iframe hook below never leaks into code that imports DOMPurify directly.
// Every sanitizing in the app goes through this file: sanitizeHtml, sanitizeDocumentFragment, v-safe-html.
const purify = DOMPurify(window)

// Embeds the admin editor's `media` plugin can produce. Any other iframe is dropped.
// Scheme is optional: TinyMCE 4 saved embeds protocol-relative (`//www.youtube.com/embed/...`);
// legacy http:// embeds are upgraded to https in the hook below.
const ALLOWED_IFRAME_SRC = /^(https?:)?\/\/(www\.)?(youtube\.com\/embed\/|youtube-nocookie\.com\/embed\/|player\.vimeo\.com\/video\/|google\.com\/maps\/embed|maps\.google\.com\/maps\?)/i

// Detaching the current node from an uponSanitizeElement hook is supported: DOMPurify treats it
// as removed and stops processing it (dompurify README "Hooks"; checked against 3.4.17). It is
// not listed in `purify.removed`, which nothing here reads.
purify.addHook("uponSanitizeElement", (node, data) => {
  if (data.tagName !== "iframe") return
  const src = node.getAttribute("src") || ""
  if (!ALLOWED_IFRAME_SRC.test(src)) {
    node.remove()
  } else if (/^http:/i.test(src)) {
    // Legacy http:// embeds would be blocked as mixed content on the https site.
    node.setAttribute("src", src.replace(/^http:/i, "https:"))
  }
})

// TinyMCE 7+ adds an empty `sandbox=""` to embeds it does not recognise (Google Maps among them),
// which renders them blank. Drop only that one; a sandbox an admin set with tokens is kept.
purify.addHook("uponSanitizeAttribute", (node, data) => {
  if (data.attrName === "sandbox" && !data.attrValue.trim()) data.keepAttr = false
})

// <style> would leak CSS across the whole app; the rest embed or submit content.
const FORBID_TAGS = ["style", "script", "object", "embed", "form", "input", "base"]
const FORBID_ATTR = ["srcdoc"]

const CONFIG = {
  FORBID_TAGS,
  FORBID_ATTR,
  ADD_TAGS: ["iframe"],
  // `target` keeps admin links opening in a new tab (browsers imply noopener for _blank).
  // `referrerpolicy` is emitted on iframes by TinyMCE 7+. `sandbox` is kept unless empty (hook above).
  ADD_ATTR: ["target", "allow", "allowfullscreen", "frameborder", "referrerpolicy", "sandbox"],
}

// Admin documents (T&C, privacy policy): no embeds at all, and no `target`, so a link can never
// open a tab that reaches back via window.opener.
const DOCUMENT_CONFIG = {
  FORBID_TAGS: [...FORBID_TAGS, "iframe"],
  FORBID_ATTR: [...FORBID_ATTR, "target"],
  RETURN_DOM_FRAGMENT: true,
}

/**
 * Sanitizes HTML before it is bound with v-html. Keeps formatting, inline styles,
 * links, id/class and allowlisted video/map embeds; strips scripts, event handlers
 * and javascript: URLs.
 * @param {string} html
 * @returns {string}
 */
export function sanitizeHtml(html) {
  if (!html) return ""
  return purify.sanitize(html, CONFIG)
}

/**
 * Sanitizes an admin-authored document body (T&C, privacy policy) into a DOM fragment, for
 * callers that post-process the nodes before rendering. Stricter than sanitizeHtml: no iframes,
 * no `target`.
 * @param {string} html
 * @returns {DocumentFragment}
 */
export function sanitizeDocumentFragment(html) {
  return purify.sanitize(html || "", DOCUMENT_CONFIG)
}

/**
 * Escapes a plain-text value (e.g. a property name) before it is interpolated into
 * an HTML string such as a vue-i18n message rendered with v-html. vue-i18n does not
 * escape parameters, and sanitizing afterwards would strip text that looks like a tag.
 * @param {*} value
 * @returns {string}
 */
export function escapeHtml(value) {
  if (value === null || value === undefined) return ""
  return String(value).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;",
  })[c])
}
